import { useMemo, useState } from 'react'
import { Box, Group, Table, Text, TextInput, Tooltip, UnstyledButton } from '@mantine/core'
import { IconChevronDown, IconChevronUp, IconSearch } from '@tabler/icons-react'

import type { SlowQuery } from '@/api/database'

import { QueryPanel } from './QueryPanel'
import {
  type Coluna,
  type Ordem,
  filtrarQueries,
  medido,
  ordenarQueries,
  proximaOrdem,
  rotuloDaJanela,
} from './ordenacao'

function truncate(text: string, max: number): string {
  return text.length > max ? text.slice(0, max) + '…' : text
}

/** Cabeçalho clicável. Três estados: asc, desc e a ordem do servidor (carga). */
function Cabecalho({
  coluna,
  ordem,
  onClick,
  children,
  alinharDireita = false,
}: {
  coluna: Coluna
  ordem: Ordem
  onClick: (c: Coluna) => void
  children: React.ReactNode
  alinharDireita?: boolean
}) {
  const ativa = ordem?.coluna === coluna
  return (
    <Table.Th ta={alinharDireita ? 'right' : undefined}>
      <UnstyledButton
        onClick={() => onClick(coluna)}
        style={{ width: '100%', textAlign: alinharDireita ? 'right' : 'left' }}
        aria-label={`Ordenar por ${typeof children === 'string' ? children : coluna}`}
      >
        <Group gap={4} justify={alinharDireita ? 'flex-end' : 'flex-start'} wrap="nowrap">
          <Text span inherit>{children}</Text>
          {ativa && (ordem?.direcao === 'asc' ? <IconChevronUp size={12} /> : <IconChevronDown size={12} />)}
        </Group>
      </UnstyledButton>
    </Table.Th>
  )
}

/**
 * A tabela de queries lentas: ordenável, filtrável, e honesta sobre o que está mostrando.
 *
 * **O conjunto é escolhido pela AWS, não por esta tela.** O Performance Insights devolve no máximo
 * 25 chaves e ranqueia **só por `db.load.avg`** — medido em 18/08/2026: pedir latência como métrica
 * de ranking é recusado com `The specified metric is not valid`. Então ordenar por latência
 * reordena estas 25, e não busca "as mais lentas do banco". A legenda acima da tabela diz isso, e
 * não é decoração: sem ela a tabela mente por omissão.
 */
export function SlowQueriesTable({
  itens,
  janelaSegundos,
  bancoEscolhido,
  horas,
  tenantId,
  dbIdentifier,
}: {
  itens: SlowQuery[]
  janelaSegundos?: number
  bancoEscolhido?: string | null
  /** A janela que produziu esta listagem — viaja até o painel, que a manda de volta ao backend. */
  horas: number
  tenantId: number
  dbIdentifier: string
}) {
  const [ordem, setOrdem] = useState<Ordem>(null)
  const [termo, setTermo] = useState('')
  const [aberta, setAberta] = useState<SlowQuery | null>(null)

  const visiveis = useMemo(
    () => ordenarQueries(filtrarQueries(itens, termo), ordem),
    [itens, termo, ordem],
  )

  const janelaTexto = rotuloDaJanela(janelaSegundos).replace('Chamadas ', '').replace(/[()]/g, '')

  return (
    <Box>
      <Group justify="space-between" mb="xs" align="flex-end">
        <Text size="xs" c="dimmed" maw={620}>
          As {itens.length} queries de maior carga {janelaTexto ? `nas últimas ${janelaTexto}` : ''}
          {bancoEscolhido ? ` no banco ${bancoEscolhido}` : ''}. Ordenar reordena estas {itens.length} —
          o Performance Insights só sabe ranquear por carga.
        </Text>
        <TextInput
          size="xs"
          w={260}
          leftSection={<IconSearch size={14} />}
          placeholder="Filtrar por trecho da query"
          aria-label="Filtrar por trecho da query"
          value={termo}
          onChange={(e) => setTermo(e.currentTarget.value)}
        />
      </Group>

      {termo.trim() && (
        <Text size="xs" c="dimmed" mb="xs">
          {visiveis.length} de {itens.length} linhas
        </Text>
      )}

      {termo.trim() && visiveis.length === 0 ? (
        // Estado vazio do FILTRO: nunca reaproveitar o alerta de "PI desabilitado", que mandaria
        // mexer na configuracao do RDS por causa de uma busca.
        <Text c="dimmed" ta="center" py="md" size="sm">
          Nenhuma das {itens.length} queries de maior carga contém “{termo.trim()}”. A busca vale
          para esta lista: o Performance Insights não permite procurar em todo o histórico.
        </Text>
      ) : (
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Cabecalho coluna="query" ordem={ordem} onClick={(c) => setOrdem(proximaOrdem(ordem, c))}>
                Query
              </Cabecalho>
              <Cabecalho coluna="avg_load" ordem={ordem} onClick={(c) => setOrdem(proximaOrdem(ordem, c))} alinharDireita>
                Avg Load
              </Cabecalho>
              <Table.Th ta="right">
                <Tooltip
                  label="Derivado da taxa média de chamadas por segundo medida na janela. “—” significa que o Performance Insights não devolveu a métrica para esta query."
                  multiline
                  maw={320}
                  withArrow
                >
                  <span>
                    <UnstyledButton
                      onClick={() => setOrdem(proximaOrdem(ordem, 'calls'))}
                      aria-label="Ordenar por chamadas"
                    >
                      <Group gap={4} justify="flex-end" wrap="nowrap">
                        <Text span inherit>{rotuloDaJanela(janelaSegundos)}</Text>
                        {ordem?.coluna === 'calls' &&
                          (ordem.direcao === 'asc' ? <IconChevronUp size={12} /> : <IconChevronDown size={12} />)}
                      </Group>
                    </UnstyledButton>
                  </span>
                </Tooltip>
              </Table.Th>
              <Cabecalho coluna="avg_latency_ms" ordem={ordem} onClick={(c) => setOrdem(proximaOrdem(ordem, c))} alinharDireita>
                Latência média (ms)
              </Cabecalho>
              <Cabecalho coluna="total_time_ms" ordem={ordem} onClick={(c) => setOrdem(proximaOrdem(ordem, c))} alinharDireita>
                Total Time (ms)
              </Cabecalho>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {visiveis.map((sq, idx) => (
              <Table.Tr
                key={idx}
                onClick={() => setAberta(sq)}
                style={{ cursor: 'pointer' }}
                aria-label="Abrir query completa"
              >
                {/* Sem Tooltip: era ele que impedia selecionar o texto. A query inteira vive no
                    painel, que abre no clique. */}
                <Table.Td style={{ maxWidth: 400 }}>
                  <Text size="sm" style={{ fontFamily: 'monospace' }}>
                    {truncate(sq.query_text ?? '', 80)}
                  </Text>
                </Table.Td>
                <Table.Td ta="right"><Text size="sm">{sq.avg_load.toFixed(4)}</Text></Table.Td>
                <Table.Td ta="right"><Text size="sm">{medido(sq.calls)}</Text></Table.Td>
                <Table.Td ta="right"><Text size="sm">{medido(sq.avg_latency_ms, 4)}</Text></Table.Td>
                <Table.Td ta="right"><Text size="sm">{medido(sq.total_time_ms, 2)}</Text></Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}

      <QueryPanel
        query={aberta}
        janelaSegundos={janelaSegundos}
        horas={horas}
        tenantId={tenantId}
        dbIdentifier={dbIdentifier}
        onClose={() => setAberta(null)}
      />
    </Box>
  )
}
